import { payerTypeConstants } from "constants/payer";

/**
 * CLE-2751 -- a contract can lend its rates to other payers, stored on
 * `medicareAdvantagePayerIds`. The field keeps its original name (see the comment on the
 * column in schema.prisma) but now carries every kind of link, keyed by the linked payer's
 * type:
 *
 *   Original Medicare contract -> Medicare Advantage payers  (the original use)
 *   Medicaid contract          -> other Medicaid payers      (one master Medicaid contract)
 *
 * Mirrors `services/clear-api/src/domains/_shared/linkedPayers.ts`; keep the two in step.
 */
const LINKED_PAYER_CONTRACT_TYPE: Record<string, string> = {
  [payerTypeConstants.MEDICARE_ADVANTAGE]: payerTypeConstants.MEDICARE,
  [payerTypeConstants.MEDICAID]: payerTypeConstants.MEDICAID,
};

/** The payer type a contract's linked payers must have, given the contract's own payer type. */
export const linkedPayerTypeForContractPayer = (
  contractPayerType?: string
): string | null => {
  if (!contractPayerType) return null;
  const match = Object.keys(LINKED_PAYER_CONTRACT_TYPE).find(
    (linkedType) => LINKED_PAYER_CONTRACT_TYPE[linkedType] === contractPayerType
  );
  return match ?? null;
};

/** Whether a contract with this payer can lend its rates to other payers at all. */
export const contractCanLinkPayers = (contractPayerType?: string): boolean =>
  linkedPayerTypeForContractPayer(contractPayerType) !== null;

export interface ILinkedPayerCopy {
  title: string;
  help: string;
  placeholder: string;
  note: string;
}

/** Card wording, which differs between the Medicare and Medicaid cases. */
export const linkedPayerCopy = (contractPayerType?: string): ILinkedPayerCopy =>
  contractPayerType === payerTypeConstants.MEDICAID
    ? {
        title: "Linked Medicaid Payers",
        help: "Select the Medicaid payers that should use this contract's rates.",
        placeholder: "Select Medicaid payers",
        note: "When processing estimates with these Medicaid payers, this contract's rates will be used instead of their own.",
      }
    : {
        title: "Medicare Advantage Payers",
        help: "Select Medicare Advantage payers that should use this Medicare contract's rates.",
        placeholder: "Select Medicare Advantage payers",
        note: "When processing estimates with these Medicare Advantage payers, this contract's rates will be used instead of their own.",
      };
